import { assign, createMachine, send } from 'xstate';
import { choose } from 'xstate/lib/actions';
import { SecondaryIdFieldMetadata } from '~/components/inputs/secondary-identifier/secondary-identifier.component';
import { BUSINESS } from '~/funnels/business/config';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { I_SOLAR_UCB } from '~/funnels/isolar-ucb/config';
import { setError } from '~/machines/utils/actions';
import { contextCreator } from '~/machines/utils/context';
import { LDClient, LDFlags } from '~/providers';
import { ParsedQueryParams } from '~/providers/query-params';
import { PagesRequired } from '~/screens/bill-upload';
import {
  createMeter,
  deleteBillUpload,
  fetchNyLmiZoneOptions,
  fetchUtilityConnectToken,
  updateAmerenCredential,
  updateUtilityAccount,
  uploadBillImage,
  fetchIsInLmiZone,
  updateConsolidatedUtilityAccount,
  updateOriginationSource,
  fetchBillingExperienceOptions,
} from '~/services';
import { FetchIsInLmiZoneParams, FetchIsInLmiZoneResponse } from '~/services/fetch-is-in-lmi-zone';
import {
  FetchNyLmiZoneOptionsParams,
  FetchNyLmiZoneOptionsResponse,
} from '~/services/fetch-ny-dac-ez-zone';
import { BillingDataSource, EnrollmentDataSource } from '~/typings/context';
import { UtilityConnectStatus } from '~/typings/utility-connect';
import { AMEREN_TIMEOUT_IN_MS, UTILITY_IDS } from '~/utils/constants';
import { addFileProps } from '~/utils/files';
import {
  allowConsolidatedUtilityAccount,
  getUserIpFromStore,
  identify,
  sendToSegment,
} from '~/utils/tracking';
import {
  EnrollmentContext,
  EnrollmentDoneData,
  EnrollmentEvent,
  EnrollmentServices,
  EnrollmentStates,
  LMIZoneOptions,
  NYLMIZoneOptions,
} from './types';

/**
 *
 * Note: Some states in the machine has `id` property.
 * Do not remove this property. Also always add `id` to new root states.
 * This is needed to transition from a child state to a parent's sibling. (e.g)
 * assume the following Machine
 *
 * ----------
 * Machine M:
 * ----------
 * |_A
 * |_|__A1 => // invoke some service, onDone => target: #B
 * |_B
 *
 *
 * if 'B' don't have an id, The machine cannot transition from an inner state 'A1'
 * to an outer state 'B' which is the sibling of A1's parent.
 *
 * more information here: https://github.com/statelyai/xstate/issues/93#issuecomment-383557896
 */
export const enrollmentMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as EnrollmentContext,
        events: {} as EnrollmentEvent,
        services: {} as EnrollmentServices,
      },
      context: {
        advanceOn: ['verified'],
        canSkipUtilityConnect: LDClient.variation(LDFlags.skipUtilityConnect),
        enrollmentOptions: ['utility_connect', 'bill_capture', 'partner_provided'],
        errors: {},
        files: [],
        maxPages: 2 as PagesRequired,
        pagesRequired: 2 as PagesRequired,
        showUtilityConnectButton: true,
        queryParams: {} as ParsedQueryParams,
        funnelPacket: {} as EnrollmentContext['funnelPacket'],
        timeout: AMEREN_TIMEOUT_IN_MS,
      },
      initial: 'start',
      states: {
        start: {
          entry: 'trackStarted',
          always: [
            { cond: 'returnedFromAmeren', target: 'ameren' },
            { cond: 'startAtAccountNumber', target: 'accountNumber' },
            { cond: 'startAtBillUpload', target: 'billUpload' },
            { cond: 'hasDecodedIdentifier', target: 'accountNumber' },
            { target: 'utilityConnect' },
          ],
        },
        utilityConnect: {
          entry: 'selectedUtilityConnect',
          id: 'utilityConnect',
          on: {
            CHOOSE_ACCOUNT_NUMBER: 'accountNumber',
            CHOOSE_BILL_UPLOAD: 'billUpload',
            SKIP_UTILITY_CONNECT: [
              {
                cond: 'shouldSkipUtilityTimeout',
                target: '#completed',
                actions: 'selectedAccountNumber',
              },
              { target: 'completed', actions: 'selectedNone' },
            ],
          },
          initial: 'loading',
          states: {
            loading: {
              invoke: {
                src: 'fetchUtilityConnectToken',
                onDone: { target: 'idle', actions: 'saveUtilityConnectToken' },
                onError: { target: 'idle', actions: 'setErrorUtilityConnect' },
              },
            },
            idle: {
              on: {
                CREDENTIAL_RESULT: {
                  target: 'fetchingBillingExperienceOptions',
                  actions: ['saveUtilityConnectStatus', 'trackUtilityConnectResult'],
                },
              },
            },
            fetchingBillingExperienceOptions: {
              invoke: {
                src: 'fetchBillingExperienceOptions',
                onDone: { target: 'deciding', actions: ['setBillingExperienceOptions'] },
                onError: { target: 'idle', actions: 'setErrorUtilityConnect' },
              },
            },
            deciding: {
              always: [
                { cond: 'shouldAdvance', target: 'updateEnrollmentDataSource' },
                {
                  cond: 'shouldSkipUtilityTimeout',
                  target: '#nyLmiZoneOptions',
                  actions: 'selectedAccountNumber',
                },
                {
                  cond: 'shouldFallBackToBillImageUpload',
                  target: '#billUpload',
                  actions: [
                    'trackBillImageUploadFallback',
                    'setMaxPages',
                    'setPagesRequired',
                    'setShowUtilityConnectButton',
                  ],
                },
                {
                  cond: 'shouldFallBackToAccountNumber',
                  target: '#fallBackAccountNumber',
                  actions: 'trackAlternateEnrollment',
                },
                { target: 'idle' },
              ],
            },
            updateEnrollmentDataSource: {
              invoke: {
                src: 'updateUtilityAccount',
                onDone: { target: 'selectLmiZoneOptions' },
                onError: { target: 'idle', actions: 'setErrorUtilityConnect' },
              },
            },
            selectLmiZoneOptions: {
              always: [
                {
                  cond: 'shouldUseNyLmiFlow',
                  target: '#nyLmiZoneOptions',
                },
                {
                  cond: 'shouldUseLmiOptionsFlow',
                  target: '#isInLmiZone',
                },
                { target: '#completed' },
              ],
            },
          },
        },
        accountNumber: {
          entry: 'selectedAccountNumber',
          id: 'accountNumber',
          on: {
            CHOOSE_UTILITY_CONNECT: 'utilityConnect',
          },
          initial: 'idle',
          states: {
            idle: {
              on: {
                SUBMIT_UTILITY_ACCOUNT_NUMBER: {
                  target: 'submitting',
                  actions: 'saveUtilityAccountData',
                },
              },
            },
            submitting: {
              entry: ['clearErrorAccountNumber', 'trackSecondaryIdentifierAdded'],
              invoke: {
                src: 'updateUtilityAccount',
                onDone: [
                  {
                    cond: 'shouldUseNyLmiFlow',
                    target: '#nyLmiZoneOptions',
                  },
                  {
                    cond: 'shouldUseLmiOptionsFlow',
                    target: '#isInLmiZone',
                  },
                  {
                    target: '#completed',
                  },
                ],
                onError: { target: 'idle', actions: 'setErrorAccountNumber' },
              },
            },
          },
        },
        fallBackAccountNumber: {
          entry: choose([
            {
              cond: 'isDominionVAUtility',
              actions: 'selectedBillUpload',
            },
            {
              actions: ['selectedUtilityConnect', 'updateOriginationSourceEnrollmentExperience'],
            },
          ]),
          id: 'fallBackAccountNumber',
          on: {
            CHOOSE_UTILITY_CONNECT: 'utilityConnect',
          },
          initial: 'idle',
          states: {
            idle: {
              on: {
                SUBMIT_UTILITY_ACCOUNT_NUMBER: {
                  target: 'submitting',
                  actions: 'saveUtilityAccountData',
                },
              },
            },
            submitting: {
              entry: ['clearErrorAccountNumber', 'trackSecondaryIdentifierAdded'],
              invoke: {
                src: 'updateUtilityAccount',
                onDone: [
                  {
                    cond: 'shouldUseNyLmiFlow',
                    target: '#nyLmiZoneOptions',
                  },
                  {
                    cond: 'shouldUseLmiOptionsFlow',
                    target: '#isInLmiZone',
                  },
                  {
                    target: '#completed',
                  },
                ],
                onError: { target: 'idle', actions: 'setErrorFallBackAccountNumber' },
              },
            },
          },
        },
        billUpload: {
          entry: 'selectedBillUpload',
          id: 'billUpload',
          initial: 'idle',
          on: {
            CHOOSE_UTILITY_CONNECT: 'utilityConnect',
            SUBMIT_BILLS: [
              {
                cond: 'shouldGotoFallbackAfterBillUpload',
                target: 'fallBackAccountNumber',
                actions: 'setHideDualBillExplainer',
              },
              {
                target: 'completed',
              },
            ],
          },
          states: {
            idle: {
              on: {
                DELETE_FILE: { target: 'deleting' },
                UPLOAD_FAILURE: { actions: 'storeRejectedFile' },
                UPLOAD_SUCCESS: { target: 'submitting', actions: 'storeUploadedFile' },
              },
            },
            submitting: {
              invoke: {
                src: 'saveFile',
                onDone: { target: 'idle', actions: 'updateSavedFiles' },
              },
            },
            deleting: {
              invoke: {
                src: 'deleteFile',
                onDone: { target: 'idle', actions: 'removeDeletedFile' },
              },
            },
          },
        },
        ameren: {
          entry: 'selectedUtilityConnect',
          invoke: {
            src: 'updateAmerenCredential',
            onDone: { target: 'completed', actions: 'trackAmerenSuccess' },
            onError: { target: 'utilityConnect', actions: 'setErrorUtilityConnect' },
          },
          after: {
            /* If updateAmerenCredential() did not return within AMEREN_TIMEOUT_IN_MS, initiate a timeout */
            [AMEREN_TIMEOUT_IN_MS]: {
              cond: 'shouldFallBackToAccountNumber',
              actions: send('UPDATE_AMEREN_CREDENTIALS_TIMEOUT'),
              target: 'amerenAuthTimeout',
            },
          },
        },
        amerenAuthTimeout: {
          on: {
            UPDATE_AMEREN_CREDENTIALS_TIMEOUT: {
              actions: ['setAmerenTimeoutResponse'],
              target: 'fallBackAccountNumber',
            },
          },
        },
        nyLmiZoneOptions: {
          id: 'nyLmiZoneOptions',
          invoke: {
            src: 'fetchLmiZoneOptions',
            onDone: {
              target: '#completed',
              actions: 'setNyLmiZoneOptions',
            },
            onError: { actions: 'setErrorNyLmiZoneOptions' },
          },
        },
        isInLmiZone: {
          id: 'isInLmiZone',
          invoke: {
            src: 'fetchIsInLmiZone',
            onDone: [
              {
                cond: 'vaLmiFallBackToAccountNumber',
                target: 'fallBackAccountNumber',
                actions: 'trackLMIProofOptionsValue',
              },
              {
                target: '#completed',
                actions: 'setInLmiZoneOptions',
              },
            ],
            onError: { actions: 'setErrorInLmiZoneOptions' },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          entry: ['trackCompleted', 'identifyResumingUser'],
          invoke: {
            id: 'updateFunnelStatus',
            src: 'updateFunnelStatus',
          },
          data: (context): EnrollmentDoneData => ({
            enrollmentSelection: context.enrollmentSelection!,
            nyLmiZoneOptions: context.nyLmiZoneOptions,
            lmiZoneOptions: context.lmiZoneOptions,
            utilityConnectStatus: context.utilityConnectStatus,
          }),
        },
      },
      on: {
        UPDATED_CONTEXT_FROM_PARENT: { actions: 'setUpdatedContextFromParent' },
      },
    },
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
      actions: {
        setMaxPages: assign((_context, _event) => ({
          maxPages: 2 as PagesRequired,
        })),
        setHideDualBillExplainer: assign((_context, _event) => ({
          hideDualBillExplainer: true,
        })),
        setShowUtilityConnectButton: assign((_context, _event) => ({
          showUtilityConnectButton: false,
        })),
        setPagesRequired: assign((_context, _event) => ({
          pagesRequired: 1 as PagesRequired,
        })),
        setBillingExperienceOptions: assign((_context, event) => ({
          billingExperienceOptions: (
            event as { data: { billingExperienceOptions: BillingDataSource[] } }
          ).data.billingExperienceOptions,
        })),
        clearErrorAccountNumber: assign(context => ({
          errors: { ...context.errors, accountNumber: undefined },
        })),
        saveUtilityAccountData: assign((_context, event) => ({
          address: event.data.address,
          addressSecondary: event.data.addressSecondary,
          estimatedAnnualKwh: event.data.estimatedAnnualKwh,
          estimatedMonthlyKwh: event.data.estimatedMonthlyKwh,
          month: event.data.month,
          meterNumber: event.data.meterNumber,
          secondaryIdentifier: event.data.secondaryIdentifier,
          utilityAccountNumber: event.data.utilityAccountNumber,
        })),
        saveUtilityConnectStatus: assign((_context, event) => ({
          utilityConnectStatus: event.data.utilityConnectStatus,
        })),
        setNyLmiZoneOptions: assign((_context, event) => ({
          nyLmiZoneOptions: (event as { data: { nyLmiZoneOptions: NYLMIZoneOptions } }).data
            .nyLmiZoneOptions,
        })),
        setInLmiZoneOptions: assign((_context, event) => ({
          isInLmiZone: (event as { data: { lmiZoneOptions: LMIZoneOptions } }).data.lmiZoneOptions
            .isInLmiArea,
          lmiZoneOptions: (event as { data: { lmiZoneOptions: LMIZoneOptions } }).data
            .lmiZoneOptions,
        })),
        saveUtilityConnectToken: assign((_context, event) => ({
          utilityConnectToken: event.data.utilityConnectToken,
        })),
        selectedAccountNumber: assign(_context => ({
          enrollmentSelection: 'partner_provided' as EnrollmentDataSource,
        })),
        selectedBillUpload: assign(_context => ({
          enrollmentSelection: 'bill_capture' as EnrollmentDataSource,
        })),
        selectedNone: assign(_context => ({
          enrollmentSelection: 'none' as EnrollmentDataSource,
        })),
        selectedUtilityConnect: assign(_context => ({
          enrollmentSelection: 'utility_connect' as EnrollmentDataSource,
        })),
        updateOriginationSourceEnrollmentExperience: context => {
          updateOriginationSource(
            context.funnelPacket?.user.id,
            context.funnelId,
            'partner_provided',
          );
        },
        /**
         * If the machine entered UPDATE_AMEREN_CREDENTIALS_TIMEOUT, that means that utilityConnect timed out
         * before saving the credentials we got from Ameren. This does not mean Ameren failed its OAuth.
         * We either successfully got redirected with ameranCode or we didn't. in either case, since utility
         * connect timed out, we clear the amerenCode. If we don't do this, the utilityConnect state will try
         * to check for condition 'returnedFromAmeren' every time we enter that state and assume that we just
         * got back from Ameren OAuth and have not contacted utility connect to update our credential and will
         * show the same OAuth screen to user rather than taking them to fallback screen.
         */
        setAmerenTimeoutResponse: assign((_context, _event) => ({
          amerenCode: undefined,
          utilityConnectStatus: 'timed_out' as UtilityConnectStatus,
        })),
        setErrorAccountNumber: assign((context, event) => ({
          errors: setError<EnrollmentStates>('accountNumber', context, event),
        })),
        setErrorNyLmiZoneOptions: assign((context, event) => ({
          errors: setError<EnrollmentStates>('nyLmiZoneOptions', context, event),
        })),
        setErrorInLmiZoneOptions: assign((context, event) => ({
          errors: setError<EnrollmentStates>('isInLmiZone', context, event),
        })),
        setErrorFallBackAccountNumber: assign((context, event) => ({
          errors: setError<EnrollmentStates>('fallBackAccountNumber', context, event),
        })),
        setErrorUtilityConnect: assign((context, event) => ({
          errors: setError<EnrollmentStates>('utilityConnect', context, event),
        })),
        setUpdatedContextFromParent: assign((context, event) => ({
          ...context,
          ...event.data,
        })),
        removeDeletedFile: assign((context, event) => ({
          files: context.files.filter(file => file.id !== event.data.id),
        })),
        storeRejectedFile: assign((context, event) => ({
          files: [...context.files, addFileProps(event.file)],
        })),
        storeUploadedFile: assign((context, event) => ({
          files: [...context.files, addFileProps(event.file, event.base64Image)],
        })),
        updateSavedFiles: assign((context, event) => ({
          files: context.files.map(file => (event.data.id === file.id ? event.data : file)),
        })),
        trackAlternateEnrollment: context => {
          /* eslint-disable camelcase */
          if (context.funnelId === GO_SOLAR_FUNNEL_ID) {
            sendToSegment({
              type: 'utility_connect_timed_out',
              // event_message need to be exactly 'event_message' because zoho integration need it in that exact case.
              funnelPacket: context.funnelPacket,
            });
          }
          /* eslint-enable camelcase */

          sendToSegment({
            type: 'alternate_enrollment_step_started',
            enrollmentStep: 'partner_provided',
            funnelPacket: context.funnelPacket,
          });
        },
        trackBillImageUploadFallback: context => {
          sendToSegment({
            type: 'alternate_enrollment_step_started',
            enrollmentStep: 'bill_capture',
            funnelPacket: context.funnelPacket,
          });
        },
        trackSecondaryIdentifierAdded: async context => {
          const secondaryIdMetadata: Array<SecondaryIdFieldMetadata> = await LDClient.variation(
            LDFlags.showSecondaryAccountIdentifier,
          );
          /* eslint-disable camelcase */
          if (secondaryIdMetadata && secondaryIdMetadata.length !== 0) {
            const secondaryIdentifierData = secondaryIdMetadata.find(
              metadata => metadata.name === 'secondaryIdentifier',
            );

            if (secondaryIdentifierData !== undefined) {
              sendToSegment({
                type: 'secondary_identifier_added',
                funnelPacket: context.funnelPacket,
                additional: {
                  secondary_identifier_field: secondaryIdentifierData.label,
                  account_number: context?.utilityAccountNumber?.replace(/\D/g, ''),
                  secondary_account_identifier: context?.secondaryIdentifier,
                },
              });
            }
          }
          /* eslint-enable camelcase */
        },
        trackAmerenSuccess: context => {
          sendToSegment({
            type: 'utility_connect_result',
            utilityCredentialId: context.funnelPacket.utilityCredential.id,
            utilityConnectStatus: 'verified',
            funnelPacket: context.funnelPacket,
          });
        },
        trackCompleted: context => {
          sendToSegment({
            type: 'enrollment_step_completed',
            enrollmentDataSourceSelection: context.enrollmentSelection!,
            funnelPacket: context.funnelPacket,
          });
        },
        trackStarted: context => {
          sendToSegment({
            type: 'enrollment_step_started',
            enrollmentDataSourceOptions: context.enrollmentOptions,
            funnelPacket: context.funnelPacket,
          });
        },
        trackUtilityConnectResult: (context, event) => {
          sendToSegment({
            type: 'utility_connect_result',
            utilityCredentialId: context.funnelPacket.utilityCredential.id,
            utilityConnectStatus: event.data.utilityConnectStatus,
            funnelPacket: context.funnelPacket,
          });
        },
        trackLMIProofOptionsValue: context => {
          sendToSegment({
            type: 'lmi_proof_options_value',
            funnelPacket: context.funnelPacket,
            lmiProofDocsState: LDClient.variation(LDFlags.lmiProofDocsState),
          });
        },
        // Adding this identify function to fetch the discount for certain promo codes
        identifyResumingUser: context => {
          identify({
            email: context.funnelPacket.user.email,
            id: context.funnelPacket.user.id,
            utilityId: context.funnelPacket.utility.id,
            funnelTrack: context.funnelPacket.utility.funnelTrack,
            promoCode: context.promoCode || context.funnelPacket?.user?.signupPromoCode?.code,
            state: context.funnelPacket.utility.state.abbreviation,
          });
        },
      },
      /* eslint-disable @typescript-eslint/no-unused-vars */
      guards: {
        isDominionVAUtility: context => isDominionVAUtility(context),
        returnedFromAmeren: context => !!context.amerenCode,
        shouldAdvance: context =>
          context.utilityConnectStatus === 'verified' ||
          context.advanceOn.includes(context.utilityConnectStatus!),
        shouldFallBackToAccountNumber: () => false,
        shouldFallBackToBillImageUpload: () => false,
        startAtAccountNumber: context =>
          !!context.startAtAccountNumber ||
          context.enrollmentOptions[0] === 'partner_provided' ||
          !!context.isUserMissingServiceAddress,
        startAtBillUpload: context =>
          !!context.startAtBillUpload || context.enrollmentOptions[0] === 'bill_capture',
        shouldSkipUtilityTimeout: () => LDClient.variation(LDFlags.showUtiltityAccountNumberScreen),
        shouldGotoFallbackAfterBillUpload: context => isFallingBackAfterBillUpload(context),
        shouldUseLmiOptionsFlow: context =>
          isDominionVAUtility(context) || LDClient.variation(LDFlags.ffUseGeoCoderForMd),
        shouldUseNyLmiFlow: () => LDClient.variation(LDFlags.shouldUseNYLmiNewFlow),
        vaLmiFallBackToAccountNumber: (_context, event) =>
          (event as { data: { lmiZoneOptions: LMIZoneOptions } }).data.lmiZoneOptions.error,
        // Go to account number on successful decode of account number or secondary account identifier (power setter)
        hasDecodedIdentifier: context => {
          const isAccountNumberDecoded = !!(
            context.unparsedQueryParams?.encodedUtilityAccountNumber &&
            context.funnelPacket?.unparsedQueryParams?.utilityAccountNumber
          );
          const isSecondaryAccountIdentifierDecoded = !!(
            context.unparsedQueryParams?.encodedSecondaryAccountId &&
            context.funnelPacket?.unparsedQueryParams?.secondaryAccountId
          );

          return isAccountNumberDecoded || isSecondaryAccountIdentifierDecoded;
        },
      },
      services: {
        deleteFile: async (context, event) => {
          const file = context.files.find(file => file.id === event.id);
          if (file?.uploadId) await deleteBillUpload({ id: file.uploadId });
          return file!;
        },
        fetchUtilityConnectToken: async context => {
          const { utilityConnectToken } = await fetchUtilityConnectToken({
            utilityCredentialId: context.funnelPacket.utilityCredential.id,
          });
          return { utilityConnectToken };
        },
        saveFile: async (context, event) => {
          const accountId = context.funnelPacket.account.id;
          let { file: image } = event;
          const { base64Image } = event;
          try {
            const { uploadId } = await uploadBillImage({
              accountId,
              base64Image,
              utilityAccountId: context.funnelPacket.utilityAccount.id,
            });
            image = Object.assign(image, { uploadId });
          } catch (e) {
            image = Object.assign(image, { error: 'save-failed' });
          }
          return image as EnrollmentContext['files'][0];
        },
        fetchBillingExperienceOptions: async context => {
          const { billingExperienceOptions } = await fetchBillingExperienceOptions({
            /**
             * There is some issue here. Getting the correct enrollmentDataSourceName
             * is a bit challenging because we won't know it immediately after
             * utility connect. The machine has to go through 'deciding' state
             * to get it. But the deciding also determines if we should
             * show billupload for acb utilities. This is because
             * enrollmentMachine is have a lot of redundant branching.
             * (e.g) there is no need for a fallbackAccountNumber and a acccountNumber
             * state as they pretty much do the same, same goes for lmizoneoptions and so on
             * we need to clear this up before making this change properly.
             *
             * For now, I'm hardcoding it as utility_connect because
             * is when we really need the billingExperienceOptions.
             */
            enrollmentDataSourceName: 'utility_connect',
            preference: context.queryParams.preferredBillingExperience,
            utilityId: context.funnelPacket!.utility.id,
            communitySolarProjectGroupId:
              context.funnelPacket!.utilityAccount.communitySolarProjectGroup?.id,
          });
          return { billingExperienceOptions };
        },
        updateUtilityAccount: async context => {
          if (context.utilityConnectStatus === 'verified') {
            /**
             * if Utility connect is verified, only
             * enrollmentDataSourceName is updated
             * because the scraper will update address and other information.
             */
            await updateUtilityAccount({
              enrollmentDataSourceName: context.enrollmentSelection!,
              utilityAccountId: context.funnelPacket.utilityAccount.id,
            });
          } else {
            await updateUtilityAccount({
              ...context.address,
              ...(context.addressSecondary && { addressSecondary: context.addressSecondary }),
              ...(context.estimatedAnnualKwh && { estimatedAnnualKwh: context.estimatedAnnualKwh }),
              ...(context.estimatedMonthlyKwh && {
                estimatedMonthlyKwh: context.estimatedMonthlyKwh,
              }),
              ...(context.month && { month: context.month }),
              enrollmentDataSourceName: context.enrollmentSelection!,
              utilityAccountNumber: context.utilityAccountNumber!,
              ...(context.secondaryIdentifier && {
                secondaryIdentifier: context.secondaryIdentifier,
              }),
              utilityAccountId: context.funnelPacket.utilityAccount.id,
            });
            if (context.meterNumber) {
              await createMeter({
                meterNumber: context.meterNumber,
                utilityAccountId: context.funnelPacket.utilityAccount.id,
              });
            }
          }
          return undefined;
        },
        updateAmerenCredential: async context => {
          await updateAmerenCredential({
            amerenCode: context.amerenCode!,
            utilityCredentialId: context.funnelPacket.utilityCredential.id,
          });
          return undefined;
        },
        fetchLmiZoneOptions: async context => {
          let nyLmiZoneOptions: NYLMIZoneOptions;
          if (LDClient.variation(LDFlags.shouldUseNYLmiNewFlow)) {
            const params: FetchNyLmiZoneOptionsParams = {
              utilityAccountId: context.funnelPacket.utilityAccount.id,
            };

            const response: FetchNyLmiZoneOptionsResponse = await fetchNyLmiZoneOptions(params);
            nyLmiZoneOptions = {
              dacZone: response.dacZone,
              empowerZone: response.empowerZone,
              isAddressPresent: response.isAddressPresent,
            };
            return { nyLmiZoneOptions };
          } else {
            return { nyLmiZoneOptions: undefined };
          }
        },
        fetchIsInLmiZone: async context => {
          const shouldUseLmiFlow =
            isDominionVAUtility(context) || LDClient.variation(LDFlags.ffUseGeoCoderForMd);
          if (shouldUseLmiFlow) {
            const params: FetchIsInLmiZoneParams = {
              utilityAccountId: context.funnelPacket.utilityAccount.id,
            };

            const response: FetchIsInLmiZoneResponse = await fetchIsInLmiZone(params);

            return { lmiZoneOptions: response.lmiZoneOptions };
          } else {
            return {
              lmiZoneOptions: {
                isInLmiArea: false,
                isInCensusTract: false,
                error: false,
              },
            };
          }
        },
        updateFunnelStatus: async context => {
          const promoCode =
            context.funnelPacket?.user.signupPromoCode?.code ||
            context.funnelPacket?.user.promoCode;

          if (allowConsolidatedUtilityAccount(promoCode)) {
            const { utilityConnectStatus } = context;
            let funnelStatus = 'Utility Connect Complete';
            if (utilityConnectStatus === 'verified') {
              funnelStatus = 'Utility Connect Complete';
            } else if (utilityConnectStatus === 'rejected') {
              funnelStatus = 'Utility Connect Incomplete';
            } else if (utilityConnectStatus === 'timed_out') {
              funnelStatus = 'Utility Connect Incomplete';
            } else if (utilityConnectStatus === 'error') {
              funnelStatus = 'Utility Connect Incomplete';
            }
            const utilityAccountId = context.funnelPacket?.utilityAccount.id!;
            const updateInput = {
              funnelStatus,
              ipAddress: getUserIpFromStore(),
            };

            await updateConsolidatedUtilityAccount(utilityAccountId, updateInput);
          }
        },
      },
    },
  );

export const isFallingBackAfterBillUpload = (context: EnrollmentContext) => {
  return (
    isDominionVAUtility(context) ||
    LDClient.variation(LDFlags.ffUseGeoCoderForMd) ||
    (context.billingExperienceOptions?.includes('acb') &&
      ['timed_out', 'error', 'rejected'].includes(context.utilityConnectStatus!))
  );
};

export const enrollmentContext = contextCreator<EnrollmentContext, 'funnelPacket'>(
  enrollmentMachine,
  queryParams => ({
    amerenCode: queryParams.amerenUser?.code,
  }),
);

const isDominionVAUtility = (context: EnrollmentContext) => {
  return (
    (context.funnelPacket.utility.id === UTILITY_IDS['Dominion Energy VA'] &&
      [I_SOLAR_UCB, GO_SOLAR_FUNNEL_ID, COMMUNITY_SOLAR_FUNNEL_ID, BUSINESS].includes(
        context.funnelId!,
      )) ||
    LDClient.variation(LDFlags.shouldUseVALmiFlow)
  );
};
